import React, { useContext } from "react";
import { useNavigate } from "react-router-dom";
import { DynamicContext } from "../../Context/DynamicContext";
import goodFitIcon from "../../Assets/Images/Goodfit-badge-icon.svg";
import partialFitIcon from "../../Assets/Images/Partialfit-badge-icon.svg";
import topFitIcon from "../../Assets/Images/Topfit-badge-icon.svg";
import unlikelyFitIcon from "../../Assets/Images/Unlikelyfit-badge-icon.svg";
import networkIcon from "../../Assets/Images/Network-badge-icon.svg";
import matchUploadIcon from "../../Assets/Images/match-upload-outline.svg";
import matchLightBulbIcon from "../../Assets/Images/match-light-bulb.svg";
import Button from "../Button/Button";


const Card = ({ color, title, text, icon, isDarkMode }) => {
  const cardBackground = isDarkMode
    ? "rgba(255,255,255,0.05)"
    : "#fafafb";
  const textColor = isDarkMode ? "#9ca3af" : "#4b5563";

  return (
    <div
      className="match-percentage-card"
      style={{
        ...styles.card,
        borderLeft: `4px solid ${color}`,
        background: cardBackground,
        opacity: 1,
      }}
    >
      <img src={icon} alt={title} style={styles.icon} />
      <div style={{ flex: 1 }}>
        <h4
          style={{
            ...styles.heading,
            color: color,
          }}
        >
          {title}
        </h4>
        <p style={{ ...styles.text, color: textColor }}>{text}</p>
      </div>
    </div>
  );
};

const MatchPercentage = ({ onClose, isRerank = false }) => {
  const { isDarkMode } = useContext(DynamicContext);
  const navigate = useNavigate();

  const handleUploadClick = () => {
    navigate("/resume-parser");
    onClose();
  };

  return (
    <>
      <style>{responsiveStyles}</style>
      <div style={styles.overlay}>
        <div
          className="match-percentage-modal"
          style={{
            ...styles.container,
            background: isDarkMode ? "#1e1e1e" : "#ffffff",
            color: isDarkMode ? "#ffffff" : "#1f2937",
          }}
        >
          <div style={styles.header}>
            <h3 style={styles.title}>What do these fit labels mean?</h3>
            <button
              style={{
                ...styles.closeBtn,
                color: isDarkMode ? "#fff" : "#000",
              }}
              onClick={onClose}
              aria-label="Close"
            >
              x
            </button>
          </div>

          <div className="match-percentage-content" style={styles.contentWrapper}>
            <div className="match-percentage-left" style={styles.leftSection}>
              <Card
                color="#22c55e"
                icon={topFitIcon}
                title={isRerank ? "Top Fit" : "Top Fit (90-100%)"}
                text="Strong match on skills, experience and role."
                isDarkMode={isDarkMode}
              />
              <Card
                color="#3b82f6"
                icon={goodFitIcon}
                title={isRerank ? "Good Fit" : "Good Fit (66-90%)"}
                text="Meets most requirements; minor gaps."
                isDarkMode={isDarkMode}
              />
              <Card
                color="#f59e0b"
                icon={partialFitIcon}
                title={isRerank ? "Partial Fit" : "Partial Fit (33-66%)"}
                text="Some overlap, but clear gaps remain."
                isDarkMode={isDarkMode}
              />
              <Card
                color="#ef4444"
                icon={unlikelyFitIcon}
                title={isRerank ? "Unlikely Fit" : "Unlikely Fit (1-33%)"}
                text="Little relevant overlap with the role."
                isDarkMode={isDarkMode}
              />
            </div>

            <div className="match-percentage-right" style={styles.rightSection}>
              <div
                className="match-percentage-info"
                style={{
                  ...styles.recommendationBox,
                  background: isDarkMode ? "rgba(255, 255, 255, 0.05)" : "#faf9ff",
                  borderColor: isDarkMode ? "rgba(255, 255, 255, 0.1)" : "#dcd7ff",
                }}
              >
                <div style={styles.recommendationHeader}>
                  <img src={networkIcon} alt="" style={{ width: 18, height: 18 }} />
                  <h4 style={styles.recommendationTitle}>How are recommendations generated?</h4>
                </div>
                <p style={{ ...styles.recommendationText, color: isDarkMode ? "#9ca3af" : "#4b5563" }}>
                  {isRerank
                    ? "On top of the skills, experience and role match, added intelligence refines each fitness tier."
                    : "Fit is a direct match score across skills, experience and role similarity."}
                </p>
                <div className="match-percentage-tip" style={styles.tipBox}>
                  <img src={matchLightBulbIcon} alt="" style={{ width: 18, height: 18 }} />
                  <span>Seeing mostly unlikely fits? <b>Add more candidates</b> for better matches.</span>
                </div>
                <Button
                  label="Upload Candidates"
                  event={handleUploadClick}
                  iconSrc={matchUploadIcon}
                  iconWidth="18px"
                  style={styles.uploadBtn}
                  backgroundColor="#6d5efc"
                />
              </div>
            </div>
          </div>
        </div>
      </div>
    </>
  );
};

const styles = {
  overlay: {
    position: "fixed",
    top: 0,
    left: 0,
    inset: 0,
    background: "rgba(0,0,0,0.6)",
    display: "flex",
    alignItems: "center",
    justifyContent: "center",
    zIndex: 999,
    padding: "16px",
    boxSizing: "border-box",
  },
  container: {
    width: "clamp(700px, 52vw, 860px)",
    maxHeight: "calc(100dvh - 32px)",
    overflowY: "auto",
    borderRadius: "18px",
    padding: "24px",
    boxShadow: "0 10px 40px rgba(0,0,0,0.15)",
    fontFamily: "Inter, sans-serif",
    boxSizing: "border-box",
  },
  header: {
    display: "flex",
    justifyContent: "space-between",
    alignItems: "center",
    marginBottom: "16px",
  },
  title: {
    fontSize: "16px",
    fontWeight: "600",
    margin: 0,
  },
  closeBtn: {
    border: "none",
    background: "transparent",
    fontSize: "20px",
    lineHeight: 1,
    cursor: "pointer",
    opacity: 0.7,
    padding: "4px",
  },
  card: {
    display: "flex",
    alignItems: "center",
    gap: "12px",
    padding: "12px 14px",
    borderRadius: "12px",
    marginBottom: "10px",
    border: "none",
    background: "#fff",
    boxSizing: "border-box",
  },
  icon: {
    fontSize: "18px",
    width: "26px",
    flex: "0 0 auto",
  },
  heading: {
    margin: 0,
    fontSize: "14px",
    fontWeight: "600",
  },
  text: {
    margin: "4px 0 0",
    fontSize: "12px",
    lineHeight: "16px",
  },
  contentWrapper: {
    display: "grid",
    gridTemplateColumns: "minmax(0, 1fr) minmax(0, 1fr)",
    gap: "16px",
    alignItems: "stretch",
  },
  leftSection: {
    display: "flex",
    flexDirection: "column",
    gap: "10px",
    minWidth: 0,
  },
  rightSection: {
    minWidth: 0,
  },
  recommendationBox: {
    border: "1px solid #dcd7ff",
    borderRadius: "16px",
    padding: "18px",
    background: "#faf9ff",
    height: "100%",
    display: "flex",
    flexDirection: "column",
    boxSizing: "border-box",
  },
  recommendationHeader: {
    display: "flex",
    alignItems: "center",
    gap: "8px",
    marginBottom: "10px",
  },
  recommendationTitle: {
    margin: 0,
    fontSize: "13px",
    fontWeight: 600,
    color: "#5b5ce2",
  },
  recommendationText: {
    fontSize: "12px",
    color: "#4b5563",
    lineHeight: "19px",
    marginBottom: "14px",
  },
  tipBox: {
    display: "flex",
    alignItems: "center",
    gap: "10px",
    background: "#f3f0ff",
    border: "1px solid #ddd6fe",
    padding: "10px 12px",
    borderRadius: "10px",
    fontSize: "12px",
    color: "#5b5ce2",
    marginBottom: "14px",
  },
  uploadBtn: {
    width: "100%",
    height: "42px",
    marginTop: "auto",
    border: "none",
    borderRadius: "10px",
    background: "#6d5efc",
    color: "#fff",
    fontWeight: 600,
    cursor: "pointer",
    boxSizing: "border-box",
  },
};

const responsiveStyles = `
  .match-percentage-modal::-webkit-scrollbar {
    width: 6px;
  }

  .match-percentage-modal::-webkit-scrollbar-thumb {
    background: rgba(148, 163, 184, 0.55);
    border-radius: 999px;
  }

  .match-percentage-left .match-percentage-card {
    margin-bottom: 0 !important;
  }

  .match-percentage-tip img {
    flex: 0 0 auto;
  }

  @media (max-width: 820px) {
    .match-percentage-modal {
      width: min(94vw, 560px) !important;
    }

    .match-percentage-content {
      grid-template-columns: 1fr !important;
      gap: 12px !important;
    }

    .match-percentage-info {
      height: auto !important;
    }
  }

  @media (max-width: 520px) {
    .match-percentage-modal {
      width: 100% !important;
      max-height: calc(100dvh - 20px) !important;
      border-radius: 14px !important;
      padding: 18px !important;
    }

    .match-percentage-content {
      gap: 10px !important;
    }

    .match-percentage-card {
      padding: 13px !important;
      gap: 10px !important;
      border-radius: 12px !important;
    }

    .match-percentage-info {
      padding: 14px !important;
      border-radius: 12px !important;
    }
  }
`;

export default MatchPercentage;
